<template>
  <div class="image-compare-page page-container">
    <section class="page-header">
      <h1>图片对比</h1>
      <p>上传两张图片进行左右对照。图片仅在当前浏览器中读取，不会上传到服务器。</p>
    </section>

    <section class="compare-toolbar">
      <label class="upload-button"><i class="el-icon-upload" /> {{ leftImage ? '更换左图' : '上传左图' }}<input type="file" accept="image/*" @change="loadImage($event, 'left')" /></label>
      <label class="upload-button"><i class="el-icon-upload" /> {{ rightImage ? '更换右图' : '上传右图' }}<input type="file" accept="image/*" @change="loadImage($event, 'right')" /></label>
      <span class="toolbar-spacer" />
      <el-button size="small" icon="el-icon-zoom-out" :disabled="zoom <= 0.2" @click="changeZoom(-0.1)">缩小</el-button>
      <span class="zoom-label">{{ Math.round(zoom * 100) }}%</span>
      <el-button size="small" icon="el-icon-zoom-in" :disabled="zoom >= 4" @click="changeZoom(0.1)">放大</el-button>
      <el-button size="small" icon="el-icon-refresh-left" @click="resetZoom">重置</el-button>
      <el-button size="small" icon="el-icon-full-screen" @click="toggleFullscreen">{{ isFullscreen ? '退出全屏' : '全屏对比' }}</el-button>
    </section>

    <section ref="compareArea" class="compare-area" :class="{ fullscreen: isFullscreen }">
      <div class="image-pane" tabindex="0" @click="activeSide = 'left'" @paste.stop.prevent="pasteImage($event, 'left')" @wheel.prevent="handleWheel">
        <div class="pane-title"><span>图片 A</span><small v-if="leftName">{{ leftName }}</small></div>
        <div ref="leftStage" class="image-stage" :class="{ 'has-image': leftImage }">
          <div v-if="leftImage" class="image-canvas" :style="canvasStyle('left')"><img :src="leftImage" alt="待比较图片 A" draggable="false" @load="recordImageSize($event, 'left')" /></div>
          <div v-else class="empty-state"><i class="el-icon-picture-outline" /><span>上传第一张图片</span></div>
        </div>
      </div>
      <div class="divider"><span>VS</span></div>
      <div class="image-pane" tabindex="0" @click="activeSide = 'right'" @paste.stop.prevent="pasteImage($event, 'right')" @wheel.prevent="handleWheel">
        <div class="pane-title"><span>图片 B</span><small v-if="rightName">{{ rightName }}</small></div>
        <div ref="rightStage" class="image-stage" :class="{ 'has-image': rightImage }">
          <div v-if="rightImage" class="image-canvas" :style="canvasStyle('right')"><img :src="rightImage" alt="待比较图片 B" draggable="false" @load="recordImageSize($event, 'right')" /></div>
          <div v-else class="empty-state"><i class="el-icon-picture-outline" /><span>上传第二张图片</span></div>
        </div>
      </div>
    </section>
    <p class="hint">提示：点击左侧或右侧区域后可直接粘贴图片。将鼠标放在任一图片区域滚动滚轮，即可同时缩放两张图片。</p>
  </div>
</template>

<script>
export default {
  name: 'ImageComparePage',
  data() { return { leftImage: '', rightImage: '', leftName: '', rightName: '', leftSize: null, rightSize: null, activeSide: 'left', zoom: 1, isFullscreen: false } },
  mounted() { document.addEventListener('fullscreenchange', this.syncFullscreen); document.addEventListener('paste', this.handleDocumentPaste) },
  beforeDestroy() { document.removeEventListener('fullscreenchange', this.syncFullscreen); document.removeEventListener('paste', this.handleDocumentPaste) },
  methods: {
    loadImage(event, side) {
      const file = event.target.files && event.target.files[0]
      if (!file) return
      this.readImage(file, side)
      event.target.value = ''
    },
    readImage(file, side) {
      if (!file.type.startsWith('image/')) { this.$message.warning('请选择图片文件'); return }
      const reader = new FileReader()
      reader.onload = () => { this[`${side}Image`] = reader.result; this[`${side}Name`] = file.name || '已粘贴图片'; this[`${side}Size`] = null; this.activeSide = side; this.resetZoom() }
      reader.readAsDataURL(file)
    },
    pasteImage(event, side) { const file = this.clipboardImage(event); if (file) this.readImage(file, side) },
    handleDocumentPaste(event) { const file = this.clipboardImage(event); if (file) this.readImage(file, this.activeSide) },
    clipboardImage(event) { return Array.from(event.clipboardData && event.clipboardData.items || []).map(item => item.kind === 'file' ? item.getAsFile() : null).find(file => file && file.type.startsWith('image/')) },
    recordImageSize(event, side) { this[`${side}Size`] = { width: event.target.naturalWidth, height: event.target.naturalHeight } },
    canvasStyle(side) {
      const size = this[`${side}Size`]
      const stage = this.$refs[`${side}Stage`]
      if (!size || !stage) return {}
      const fitScale = Math.min((stage.clientWidth - 32) / size.width, (stage.clientHeight - 32) / size.height, 1)
      return { width: `${Math.max(1, size.width * fitScale * this.zoom)}px`, height: `${Math.max(1, size.height * fitScale * this.zoom)}px` }
    },
    changeZoom(delta) { this.zoom = Math.min(4, Math.max(0.2, Number((this.zoom + delta).toFixed(2)))) },
    handleWheel(event) { this.changeZoom(event.deltaY < 0 ? 0.1 : -0.1) },
    resetZoom() { this.zoom = 1 },
    toggleFullscreen() {
      if (document.fullscreenElement) { document.exitFullscreen().catch(() => {}) } else if (this.$refs.compareArea.requestFullscreen) this.$refs.compareArea.requestFullscreen().catch(() => this.$message.warning('当前浏览器不支持全屏模式'))
    },
    syncFullscreen() { this.isFullscreen = document.fullscreenElement === this.$refs.compareArea }
  }
}
</script>

<style scoped>
.image-compare-page { padding-top: 32px; padding-bottom: 80px; }
.compare-toolbar { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; padding: 32px 0 16px; }
.upload-button { display: inline-flex; align-items: center; gap: 6px; padding: 8px 14px; color: #fff; font-size: 13px; cursor: pointer; border-radius: var(--r-md); background: var(--brand); transition: background-color .14s; }
.upload-button:hover { background: var(--brand-hover); }
.upload-button input { display: none; }
.toolbar-spacer { flex: 1; }
.zoom-label { min-width: 46px; color: var(--ink-2); font-size: 13px; text-align: center; }
.compare-area { display: flex; min-height: 530px; overflow: hidden; border: 1px solid var(--line); border-radius: var(--r-lg); background: var(--surface-2); box-shadow: var(--shadow-1); }
.compare-area.fullscreen { width: 100vw; height: 100vh; min-height: 0; border: 0; border-radius: 0; box-shadow: none; background: #121822; }
.image-pane { display: flex; flex: 1; flex-direction: column; min-width: 0; outline: none; }
.pane-title { display: flex; justify-content: space-between; align-items: center; padding: 12px 16px; color: var(--ink); border-bottom: 1px solid var(--line); background: var(--surface); }
.pane-title small { overflow: hidden; max-width: 55%; color: var(--ink-3); text-overflow: ellipsis; white-space: nowrap; }
.image-stage { position: relative; display: flex; flex: 1; align-items: center; justify-content: center; overflow: auto; min-height: 0; background-color: var(--surface-3); background-image: linear-gradient(45deg, var(--surface-2) 25%, transparent 25%), linear-gradient(-45deg, var(--surface-2) 25%, transparent 25%), linear-gradient(45deg, transparent 75%, var(--surface-2) 75%), linear-gradient(-45deg, transparent 75%, var(--surface-2) 75%); background-size: 20px 20px; background-position: 0 0, 0 10px, 10px -10px, -10px 0; }
.image-stage.has-image { display: block; }
.image-canvas { margin: auto; box-shadow: var(--shadow-2); }
.image-canvas img { display: block; width: 100%; height: 100%; }
.divider { display: flex; align-items: center; justify-content: center; width: 1px; background: var(--line); }
.divider span { position: absolute; z-index: 1; display: grid; place-items: center; width: 34px; height: 34px; color: #fff; font-size: 11px; font-weight: 700; border-radius: 50%; background: var(--brand); }
.empty-state { display: flex; flex-direction: column; gap: 12px; align-items: center; color: var(--ink-3); }
.empty-state i { font-size: 42px; }
.hint { margin: 12px 0 0; color: var(--ink-3); font-size: 13px; text-align: center; }
@media(max-width:700px){ .toolbar-spacer { display: none; width: 100%; flex-basis: 100%; } .compare-area { flex-direction: column; min-height: 720px; } .divider { width: 100%; height: 1px; } .image-pane { min-height: 340px; } .pane-title small { max-width: 50%; } .compare-area.fullscreen { flex-direction: column; height: 100vh; min-height: 0; } .compare-area.fullscreen .image-pane { min-height: 0; } }
</style>
